import React, { useState } from "react";
import { Collapse, Table, Tag, Button, Space, message } from "antd";
import { CaretRightOutlined, InfoCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import axios from "axios";
import DragUpload from "../Form/DragUpload";
import { formatAmount } from "../../helpers/billingStatement";

// Scoped to one noteType per page -- the Credit Notes page only ever creates CreditNotes from an
// uploaded file, the Debit Notes page only ever creates DebitNotes, even if the same file
// happens to contain both directions (rows of the other direction come back as skipped errors
// below, see BillingNoteImportService on the API).
//
// Two-step flow, same shape as the Payout page's bulk upload: uploading the file only previews
// what would be created (nothing is saved yet) -- Finance reviews the grouped list below, then
// explicitly confirms before anything is actually created and issued.
const groupColumns = [
  { title: "Tracking No.", dataIndex: "trackingNumber", key: "trackingNumber" },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (v) => formatAmount(v),
  },
  { title: "Method", dataIndex: "method", key: "method", render: (v) => v || "-" },
  { title: "Remark", dataIndex: "remark", key: "remark", render: (v) => v || "-" },
  {
    title: "Status",
    key: "status",
    render: (_, row) =>
      row.isDuplicate ? (
        <Tag color="orange">Already imported -- will be skipped</Tag>
      ) : (
        <Tag color="green">New</Tag>
      ),
  },
];

const BillingNoteImport = ({ noteType, title, onImported }) => {
  const [preview, setPreview] = useState();
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState();

  // title is passed plural ("Credit Notes"), same convention as BillingNoteList -- singular is
  // derived here for the per-group/per-note headers below.
  const singularTitle = title.replace(/s$/, "");
  const apiSuffix = noteType === "CREDIT" ? "credit-notes" : "debit-notes";
  // Row sign is reconstructed to match noteType's expected sign -- the API's own convention
  // (BillingNoteImportService.matchesNoteType) is positive = credit, negative = debit. preview()
  // shows amount as Math.abs() for display, so this is what puts the sign back before confirm.
  const sign = noteType === "CREDIT" ? 1 : -1;

  const handlePreviewSuccess = (res) => {
    setResult(undefined);
    setPreview(res.data.data);
  };

  const handleConfirm = async () => {
    const rows = preview.groups.flatMap((group) =>
      group.rows.map((row) => ({
        trackingNumber: row.trackingNumber,
        amount: sign * Math.abs(row.amount),
        remark: row.remark,
        method: row.method,
        transactionId: row.transactionId,
      })),
    );

    if (!rows.length) {
      message.warning("Nothing to confirm -- every row was a duplicate or had an error.");
      return;
    }

    setConfirming(true);
    try {
      const res = await axios.post(`/api/${apiSuffix}/import/confirm`, { rows });
      setResult(res.data.data);
      setPreview(undefined);
      if (typeof onImported === "function") {
        onImported();
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
    >
      <Collapse.Panel header={`Bulk Import ${title} (Excel)`} key="import">
        <DragUpload
          action={`${process.env.REACT_APP_SERVER_URL}/api/${apiSuffix}/import/preview`}
          accept=".xls, .xlsx"
          method="post"
          additionalDesc={`Columns required: trackingNo, amount. ${
            noteType === "CREDIT" ? "Positive" : "Negative"
          } amounts become a ${singularTitle.toLowerCase()} line -- one note per merchant found in the file. Rows of the other direction are skipped (import them from the ${
            noteType === "CREDIT" ? "Debit" : "Credit"
          } Notes page instead). Uploading only previews the result; nothing is created until you confirm below.`}
          onUploadSuccess={handlePreviewSuccess}
        />

        {preview && (
          <div style={{ marginTop: 16 }}>
            {preview.groups.map((group) => (
              <div key={group.merchantId} style={{ marginBottom: 16 }}>
                <h4>
                  <strong>
                    {singularTitle} -- {group.merchantName} ({formatAmount(group.totalAmount)})
                  </strong>
                </h4>
                <Table
                  rowKey="trackingNumber"
                  size="small"
                  pagination={false}
                  dataSource={group.rows}
                  columns={groupColumns}
                />
              </div>
            ))}
            {preview.errors?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4>
                  <strong>Errors ({preview.errors.length})</strong>
                </h4>
                <ul>
                  {preview.errors.map((error, index) => (
                    <li key={index}>
                      Row {error.row} ({error.trackingNumber}): {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Space>
              <Button type="primary" loading={confirming} onClick={handleConfirm}>
                Confirm Import
              </Button>
              <Button onClick={() => setPreview(undefined)}>Cancel</Button>
            </Space>
          </div>
        )}

        {result && (
          <div style={{ marginTop: 16 }}>
            <Collapse
              size="small"
              defaultActiveKey={result.errors?.length ? ["result"] : undefined}
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            >
              <Collapse.Panel
                header={
                  <span>
                    <InfoCircleOutlined /> Results of Last Import ({result.processed} row(s) processed)
                  </span>
                }
                key="result"
              >
                {result.notes?.length > 0 && (
                  <>
                    <h4>
                      <strong>{singularTitle}s Issued ({result.notes.length})</strong>
                    </h4>
                    <ul>
                      {result.notes.map((note) => (
                        <li key={note.objectId}>
                          {note.merchant?.name || note.merchant?.objectId} -- {formatAmount(note.originalAmount, note.currency)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {result.errors?.length > 0 && (
                  <>
                    <h4>
                      <strong>Errors ({result.errors.length})</strong>
                    </h4>
                    <ul>
                      {result.errors.map((error, index) => (
                        <li key={index}>
                          Row {error.row} ({error.trackingNumber}): {error.message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Collapse.Panel>
            </Collapse>
          </div>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};

BillingNoteImport.propTypes = {
  noteType: PropTypes.oneOf(["CREDIT", "DEBIT"]).isRequired,
  title: PropTypes.string.isRequired,
  onImported: PropTypes.func,
};

export default BillingNoteImport;
